/**
 * Auth Service - Servicio de autenticación
 */

import { ApiServiceBase } from '../core/ApiService';
import type { User, AuthResponse, LoginRequest, MagicLinkResponse } from '../types/auth';
import logger from '../../../utils/simpleLogger';

export class AuthService extends ApiServiceBase {
  /**
   * Inicia sesión con email/username y contraseña
   */
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    logger.info('🔐 [API] login() called', {
      identifier: credentials.identifier,
    });
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (data.success && data.access_token) {
        this.tokenManager.saveTokensToStorage(
          data.access_token,
          data.refresh_token || '',
          data.user
        );
        logger.info('🔐 [API] login success', {
          userId: data.user?.id,
          email: data.user?.email,
        });
      }

      return data;
    } catch (error) {
      logger.error('❌ [API] login error', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed',
      };
    }
  }

  /**
   * Send magic link to email
   */
  async sendMagicLink(email: string): Promise<MagicLinkResponse> {
    logger.info('🔐 [API] sendMagicLink() called', { email });
    try {
      const response = await fetch(`${this.baseUrl}/auth/send-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      return await response.json();
    } catch (error) {
      logger.error('❌ [API] sendMagicLink error', error);
      return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
    }
  }

  /**
   * Verify magic link token
   */
  async verifyMagicLink(token: string): Promise<AuthResponse> {
    logger.info('🔐 [API] verifyMagicLink() called');
    try {
      const response = await fetch(`${this.baseUrl}/auth/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (data.success && data.access_token) {
        this.tokenManager.saveTokensToStorage(
          data.access_token,
          data.refresh_token || '',
          data.user
        );
        logger.info('🔐 [API] verifyMagicLink success', {
          userId: data.user?.id,
          email: data.user?.email,
        });
      }

      return data;
    } catch (error) {
      logger.error('❌ [API] verifyMagicLink error', error);
      return { success: false, error: 'Error de conexión. Intenta de nuevo.' };
    }
  }

  /**
   * Inicia sesión con Google OAuth
   */
  async googleLogin(credential: string): Promise<AuthResponse> {
    logger.info('🔐 [API] googleLogin() called');
    try {
      const response = await fetch(`${this.baseUrl}/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });

      const data = await response.json();

      if (data.success && data.access_token) {
        this.tokenManager.saveTokensToStorage(
          data.access_token,
          data.refresh_token || '',
          data.user
        );
        logger.info('🔐 [API] googleLogin success', {
          userId: data.user?.id,
          email: data.user?.email,
        });
      }

      return data;
    } catch (error) {
      logger.error('❌ [API] googleLogin error', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Google login failed',
      };
    }
  }

  /**
   * Dev auto-login (only works when backend runs in development mode)
   */
  async devLogin(): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (data.success && data.access_token) {
        this.tokenManager.saveTokensToStorage(
          data.access_token,
          data.refresh_token || '',
          data.user
        );
      }

      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Dev login failed',
      };
    }
  }

  /**
   * Cierra sesión
   */
  async logout(): Promise<void> {
    try {
      const token = this.tokenManager.getAccessToken();
      if (token) {
        await this.request('/auth/logout', { method: 'POST' });
      }
    } catch (error) {
      logger.error('❌ [API] Logout error', error);
    } finally {
      this.tokenManager.clearTokensFromStorage();
    }
  }

  /**
   * Obtiene el perfil del usuario actual
   */
  async getProfile(): Promise<{ success: boolean; user?: User; error?: string }> {
    try {
      const response = await this.request<{ success: boolean; user: User }>('/auth/me');
      if (response.success && response.user) {
        // Actualizar usuario en tokenManager
        const currentToken = this.tokenManager.getAccessToken();
        const refreshToken = this.tokenManager.getRefreshToken();
        if (currentToken && refreshToken) {
          this.tokenManager.saveTokensToStorage(currentToken, refreshToken, response.user);
        }
      }
      return response;
    } catch (error) {
      logger.error('❌ [API] getProfile error', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get profile',
      };
    }
  }
}
